import { ColumnDef } from "@tanstack/react-table";
import { Mentorship } from "@/lib/api/dashboard.ts";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header.tsx";
import { Rating } from "@/components/ui/rating.tsx";
import { MentorshipName } from "@/components/mentorships/mentorship-name.tsx";
import { formatDate } from "@/lib/utils";
import { Badge } from "../ui/badge";
import { AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const columns: Array<ColumnDef<Mentorship> & { tooltip?: string }> = [
  {
    accessorKey: "mentorship",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Mentorship" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-start gap-1">
          {row.original.has_open_escalation && (
            <Badge variant="outline" className="w-fit rounded-lg bg-red-100">
              Escalation
            </Badge>
          )}
          <MentorshipName
            mentorName={row.original.mentor_name}
            menteeName={row.original.mentee_name}
            menteeUserId={row.original.mentee_id}
            lastMessage={row.original.last_message}
            mentorProfilePictureUrl={row.original.mentor_profile_picture_url}
            menteeProfilePictureUrl={row.original.mentee_profile_picture_url}
            lastMessageDate={row.original.last_message_date}
          />
        </div>
      );
    },
    tooltip: "Name of mentor & mentee",
  },
  {
    accessorKey: "rating",
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rating" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          <Rating
            rating={parseFloat(
              parseFloat(row.original.average_rating).toFixed(2),
            )}
          />
        </div>
      );
    },
    tooltip: "Last provided rating for the mentor by the mentee",
  },
  {
    accessorKey: "teen_days_engaged",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Teen Days Engaged" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.teen_days_engaged || "-"}
        </div>
      );
    },
    tooltip:
      "Number of days the mentee has been engaged in the mentorship in selected month",
  },
  {
    accessorKey: "teen_messages",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Teen Messages" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.teen_messages}
        </div>
      );
    },
    tooltip: "Total number of messages sent by the mentee in selected month",
  },
  {
    accessorKey: "average_response_time",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Mentor Response Time" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.average_response_time
            ? parseFloat(row.original.average_response_time).toFixed(2)
            : "-"}
        </div>
      );
    },
    tooltip:
      "Average time taken by the mentor to respond to messages in minutes in selected month",
  },
  {
    accessorKey: "scheduled_average_response_time",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Scheduled Resp. Time" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.scheduled_average_response_time
            ? parseFloat(row.original.scheduled_average_response_time).toFixed(
                2,
              )
            : "-"}
        </div>
      );
    },
    tooltip:
      "Average time taken by the mentor to respond during their scheduled hours (in minutes). Defaults to 4pm-10pm EST Mon-Fri if no schedule defined.",
  },
  {
    accessorKey: "total_calls",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Video Calls" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      const avgCallDuration = parseFloat(row.original.avg_call_duration || "0");
      const totalCalls = row.original.total_calls;

      const avgCallDurationInMinutes = avgCallDuration / 60;
      const avgCallDurationString =
        avgCallDurationInMinutes === 0
          ? "0"
          : avgCallDurationInMinutes.toFixed(2);

      return (
        <div className="flex items-center justify-center">
          {totalCalls} - {avgCallDurationString} mins
        </div>
      );
    },
    tooltip:
      "Total number of video calls and average call duration in minutes in selected month",
  },
  {
    accessorKey: "last_mentor_interaction",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last Mentor Interaction" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.last_mentor_interaction
            ? formatDate(row.original.last_mentor_interaction)
            : "-"}
        </div>
      );
    },
    tooltip: "Date of the last message sent by the mentor",
  },
  {
    accessorKey: "days_in_mentorship",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Days in Mentorship" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.days_in_mentorship || "-"}
        </div>
      );
    },
    tooltip: "Number of days the mentorship has been active since the start",
  },
  {
    accessorKey: "days_to_churn",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Days to Churn" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.days_to_churn === 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="h-5 w-5 cursor-help text-red-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Mentorship will churn tonight</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            row.original.days_to_churn || "-"
          )}
        </div>
      );
    },
    tooltip: "Number of days until the mentorship is expected to churn",
  },
];
